/**
 * Form inline « Marquer comme payé » des obligations de charge (EPIC-066
 * / 66.2 — §15.D). Comportement partagé entre la page Échéances
 * (`upcoming-obligations-page`) et le bloc « À venir » du dashboard
 * (`upcoming-obligations-block`).
 *
 * Le montant saisi est persisté dans la table consultative
 * `obligation_payment` via `POST /obligations/payments`. Il alimente
 * l'état « Payés » de Finances (§31) — jamais un registre légal ni un
 * calcul fiscal/social.
 *
 * DOM-shape-agnostic : {@see mountObligationPaymentForm} n'interagit
 * qu'avec les `[data-slot=…]` du fragment cloné (qu'il s'agisse d'une
 * ligne de tableau `<tr>` ou d'un item de liste `<li>`). Aucune
 * construction HTML ici — le markup vit dans les `<template>` des pages
 * (`tpl-obligation-payment-form`).
 */

import { api } from "/js/services/api.js";
import toast from "/js/services/toast.js";

/**
 * Domaines d'obligation (calendrier §15.D) ouvrant la saisie d'un
 * montant payé. Identiques aux valeurs `ObligationKind` côté backend
 * (mapping 1:1 — décision 66.2). Assurances / abonnement / ACPRE etc.
 * sont hors périmètre V1.
 */
const CHARGE_DOMAINS = new Set(["urssaf", "tva", "cfe", "ir"]);

/**
 * Une obligation est « payable » (form inline disponible) si son domaine
 * est une charge ET qu'elle porte une `periodKey` canonique (exposée par
 * le backend depuis 66.2). En l'absence de `periodKey`, pas de saisie.
 */
export function isChargePayable(item) {
    return Boolean(item)
        && CHARGE_DOMAINS.has(item.domain)
        && typeof item.periodKey === "string"
        && item.periodKey !== "";
}

/**
 * Date du jour au format `YYYY-MM-DD` en heure locale (pas d'UTC, pour
 * rester cohérent avec la perception « aujourd'hui » du ME).
 */
export function todayIso() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

/**
 * Monte le comportement sur un fragment de form déjà cloné :
 *  - défaut + plafond de la date au jour même ;
 *  - pré-remplissage best-effort du montant (`GET …/prefill`) ;
 *  - validation client (montant > 0, date ≤ aujourd'hui) ;
 *  - soumission `POST …/payments`, toast, puis `onSuccess()`.
 *
 * @param {ParentNode} root  fragment/élément contenant les `data-slot`.
 * @param {{
 *   obligationKind: string,
 *   periodKey: string,
 *   onSuccess?: () => void,
 *   onCancel?: () => void
 * }} options
 */
export function mountObligationPaymentForm(root, { obligationKind, periodKey, onSuccess, onCancel }) {
    const formEl = root.querySelector('[data-slot="form"]');
    const amountEl = root.querySelector('[data-slot="amount"]');
    const dateEl = root.querySelector('[data-slot="date"]');
    const notesEl = root.querySelector('[data-slot="notes"]');
    const errorEl = root.querySelector('[data-slot="error"]');
    const confirmBtn = root.querySelector('[data-slot="confirm"]');
    const cancelBtn = root.querySelector('[data-slot="cancel"]');

    const today = todayIso();
    if (dateEl) {
        dateEl.value = today;
        dateEl.max = today;
    }

    // Pré-remplissage best-effort : une absence de valeur (null) ou une
    // erreur réseau laisse simplement le champ vide (saisie libre).
    api.get("/obligations/payments/prefill", { obligationKind, periodKey })
        .then((res) => {
            if (amountEl && amountEl.value === "" && res && typeof res.amount === "number") {
                amountEl.value = res.amount.toFixed(2);
            }
        })
        .catch(() => {});

    const showError = (message) => {
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
    };
    const clearError = () => {
        if (!errorEl) return;
        errorEl.textContent = "";
        errorEl.hidden = true;
    };

    const submit = async (event) => {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        clearError();

        const amount = parseFloat((amountEl?.value ?? "").replace(",", "."));
        const paidDate = dateEl?.value ?? "";
        if (!Number.isFinite(amount) || amount <= 0) {
            showError("Indiquez un montant supérieur à 0.");
            amountEl?.focus();

            return;
        }
        if (paidDate === "" || paidDate > today) {
            showError("La date de paiement ne peut pas être dans le futur.");
            dateEl?.focus();

            return;
        }

        if (confirmBtn) confirmBtn.disabled = true;
        try {
            const notes = (notesEl?.value ?? "").trim();
            await api.post("/obligations/payments", {
                obligationKind,
                periodKey,
                amount,
                paidDate,
                ...(notes !== "" ? { notes } : {}),
            });
            toast.success("Paiement enregistré.");
            if (typeof onSuccess === "function") onSuccess();
        } catch (error) {
            if (confirmBtn) confirmBtn.disabled = false;
            showError(error?.message || "Enregistrement impossible. Réessayez.");
        }
    };

    if (formEl) {
        formEl.addEventListener("submit", submit);
    } else if (confirmBtn) {
        confirmBtn.addEventListener("click", submit);
    }

    if (cancelBtn) {
        cancelBtn.addEventListener("click", (event) => {
            event.preventDefault();
            if (typeof onCancel === "function") onCancel();
        });
    }
}
